import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { cn } from '@/lib/utils';
import type { Locale } from '@/i18n';

const LABELS: Record<Locale, { label: string; short: string; flag: string }> = {
  'pt-BR': { label: 'Português (BR)', short: 'Português (BR)', flag: '🇧🇷' },
  'pt-PT': { label: 'Português (PT)', short: 'Português (PT)', flag: '🇵🇹' },
  en: { label: 'English', short: 'English', flag: '🇬🇧' },
  es: { label: 'Español', short: 'Español', flag: '🇪🇸' },
  fr: { label: 'Français', short: 'Français', flag: '🇫🇷' },
};

interface LanguageSwitcherProps {
  className?: string;
  /** Quando true, ajusta cores para fundo escuro (sobre hero photo). */
  overHero?: boolean;
  /** Quando true, renderiza os idiomas em linha (sem dropdown) — para uso dentro de containers com overflow-hidden. */
  inline?: boolean;
}

export default function LanguageSwitcher({ className, overHero = false, inline = false }: LanguageSwitcherProps) {
  const { current, change, supported } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (inline) {
    return (
      <div className={cn('flex items-center gap-1 flex-wrap', className)}>
        {supported.map((loc) => (
          <button
            key={loc}
            onClick={() => change(loc)}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm transition-colors',
              loc === current
                ? 'bg-teal-100 text-teal-700 font-semibold'
                : 'text-ink-600 hover:bg-sand-100',
            )}
          >
            <span aria-hidden>{LABELS[loc].flag}</span>
            <span>{LABELS[loc].label}</span>
          </button>
        ))}
      </div>
    );
  }

  const triggerLabel = LABELS[current].label;
  const triggerFlag = LABELS[current].flag;

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        onClick={() => setOpen((s) => !s)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-2 rounded-pill text-sm font-medium transition-colors',
          overHero
            ? 'text-sand-50 hover:bg-white/15 [text-shadow:0_1px_6px_rgba(0,0,0,0.35)]'
            : 'text-ink-700 hover:bg-sand-100',
        )}
      >
        <Globe size={16} />
        <span aria-hidden>{triggerFlag}</span>
        <span>{triggerLabel}</span>
        <ChevronDown size={14} className={cn('transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 min-w-[180px] bg-white rounded-lg shadow-lg border border-neutral-100 py-1 z-40"
        >
          {supported.map((loc) => (
            <li key={loc}>
              <button
                role="option"
                aria-selected={loc === current}
                onClick={() => {
                  change(loc);
                  setOpen(false);
                }}
                className={cn(
                  'w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-sand-100 transition-colors',
                  loc === current && 'bg-sand-100 font-semibold',
                )}
              >
                <span aria-hidden>{LABELS[loc].flag}</span>
                <span>{LABELS[loc].label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
